import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useExamShell } from "../context/ExamShellContext";
import usePageBodyClass from "../utils/usePageBodyClass";
import ExamProctor from "../utils/examProctor";
import "./Summary.css";

const $ = window.$;

export default function Summary() {
  usePageBodyClass("page-summary");
  const navigate = useNavigate();
  const shell = useExamShell();
  const shellRef = useRef(shell);
  shellRef.current = shell;

  useEffect(() => {
    // 1. Session Check
    if (localStorage.getItem("examStatus") !== "locked") {
      navigate("/login", { replace: true });
      return undefined;
    }

    // LIVE DATE & TIME CLOCK
    function updateLiveDateTime() {
      const now = new Date();
      const options = {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      };
      $("#liveDateTime").text(now.toLocaleDateString("en-US", options));
    }
    updateLiveDateTime();
    const clockTimer = setInterval(updateLiveDateTime, 1000);

    // Anti-cheat: block copy/selection (contextmenu / Escape / F12 / etc.
    // are already blocked globally by the exam shell).
    $(document).on(
      "selectstart.pageSummary copy.pageSummary paste.pageSummary cut.pageSummary dragstart.pageSummary",
      (e) => {
        e.preventDefault();
        return false;
      }
    );

    // Load Candidate Name
    const name = localStorage.getItem("candidateName") || "Candidate";
    $("#candidateName").text(name);

    // Load Summary Data
    const rawData = localStorage.getItem("examSummaryData");
    if (!rawData) {
      alert("No exam data found.");
      localStorage.removeItem("examStatus");
      navigate("/login", { replace: true });
      return () => {
        clearInterval(clockTimer);
        $(document).off(".pageSummary");
      };
    }

    const { questions, userAnswers } = JSON.parse(rawData);

    let attempted = 0;
    let skipped = 0;

    // Calculate Attempted vs Skipped
    questions.forEach((q, idx) => {
      if (userAnswers[idx] !== undefined) attempted++;
      else skipped++;
    });

    // Save to permanent admin repository before finishing
    const allSubmissions = JSON.parse(localStorage.getItem("allExamSubmissions") || "[]");
    const newSubmission = {
      id: Date.now(),
      name: name,
      questions: questions,
      userAnswers: userAnswers,
      timestamp: new Date().toLocaleString(),
    };

    if (
      !allSubmissions.some(
        (s) => s.name === name && JSON.stringify(s.userAnswers) === JSON.stringify(userAnswers)
      )
    ) {
      allSubmissions.push(newSubmission);
      localStorage.setItem("allExamSubmissions", JSON.stringify(allSubmissions));
    }

    // Update Page UI Stats
    $("#totalAttempted").text(attempted);
    $("#skippedCount").text(skipped);

    // EXIT BUTTON HANDLER
    $("#exitExamBtn").on("click.pageSummary", function () {
      localStorage.removeItem("examStatus");
      localStorage.removeItem("examSummaryData");

      ExamProctor.stop();
      shellRef.current.release();

      window.close();
      window.open("", "_self", "");
      window.close();

      setTimeout(function () {
        $("#desktopExitScreen").css("display", "flex").hide().fadeIn(300);
      }, 200);
    });

    return () => {
      clearInterval(clockTimer);
      $(document).off(".pageSummary");
      $("#exitExamBtn").off(".pageSummary");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  return (
    <div className="page-summary">
      <div id="desktopExitScreen">
        <i className="fa-solid fa-desktop fa-5x text-success mb-4"></i>
        <h2 className="fw-bold mb-2">Examination Completed</h2>
        <p className="text-secondary fs-5 mb-4" style={{ maxWidth: "500px" }}>
          Your responses have been successfully recorded in the administrator database. Your session is now
          terminated.
        </p>
        <div className="alert alert-dark border-secondary px-4 py-3 text-light">
          <i className="fa-solid fa-info-circle me-2 text-warning"></i>
          You may now safely close this browser window or tab.
        </div>
      </div>

      <nav className="navbar navbar-dark bg-dark shadow-sm">
        <div className="container">
          <span className="navbar-brand mb-0 h1">
            <i className="fa-solid fa-graduation-cap text-warning me-2"></i>Online Exam Portal
          </span>

          <div className="header-datetime d-none d-md-flex align-items-center">
            <i className="fa-regular fa-calendar-days me-2 text-warning"></i>
            <span id="liveDateTime">Loading Date &amp; Time...</span>
          </div>

          <span className="text-white small" id="candidateName">Candidate Name</span>
        </div>
      </nav>

      <div className="container my-5">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="card summary-card p-5 mb-4 text-center">
              <i className="fa-solid fa-circle-check fa-4x text-success mb-3"></i>
              <h2 className="fw-bold">Exam Submitted Successfully!</h2>
              <p className="text-muted fs-5 mb-0">Here is your overall submission summary.</p>
            </div>

            <div className="row g-4 mb-5 justify-content-center">
              <div className="col-md-6">
                <div className="stat-box bg-white border shadow-sm">
                  <h1 className="fw-bold text-primary display-4" id="totalAttempted">0</h1>
                  <span className="text-muted fw-bold text-uppercase tracking-wide">Total Attempted</span>
                </div>
              </div>
              <div className="col-md-6">
                <div className="stat-box bg-white border shadow-sm">
                  <h1 className="fw-bold text-secondary display-4" id="skippedCount">0</h1>
                  <span className="text-muted fw-bold text-uppercase tracking-wide">Unattempted</span>
                </div>
              </div>
            </div>

            <div className="text-center mt-2">
              <button className="btn btn-danger btn-lg px-5 py-3 shadow fs-5 rounded-pill fw-bold" id="exitExamBtn">
                <i className="fa-solid fa-power-off me-2"></i>Exit Exam Session
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
